import React, { useState } from "react";
import axios from "axios";
import { Star, Trash2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { fmtDate } from "./adminShared";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function ReviewsTab({ auth, reviews, setReviews }) {
  const [reviewForm, setReviewForm] = useState({ author: "", rating: 5, text: "" });

  const addReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.author || !reviewForm.text) return toast.error("Please add the customer name and review text");
    try {
      await axios.post(`${API}/admin/reviews`, { ...reviewForm, rating: Number(reviewForm.rating), source: "Google" }, auth);
      const rev = await axios.get(`${API}/reviews`);
      setReviews(rev.data || []);
      setReviewForm({ author: "", rating: 5, text: "" });
      toast.success("Review added — it now appears on the home page!");
    } catch { toast.error("Could not add review"); }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, auth);
      setReviews((l) => l.filter((r) => r.id !== id));
      toast.success("Review removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white border border-[#eee6d6] p-6 flex flex-wrap items-center justify-between gap-4" data-testid="reputation-card">
        <div>
          <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#a8842c]" /> Reputation Center
          </h3>
          <p className="text-sm text-[#8f8571] mt-1">
            Reviews for all brands are managed centrally in the Urban owner dashboard — open the Reputation tab there.
          </p>
        </div>
        <a href="https://urbanbyveergi.com/dashboard" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" className="rounded-none border-[#d8cdb5] tracking-[0.12em] uppercase text-xs h-10 px-5" data-testid="reputation-open-btn">
            <ExternalLink size={14} className="mr-2" /> Open Reputation Dashboard
          </Button>
        </a>
      </div>

      <div className="bg-white border border-[#eee6d6] p-6">
        <h3 className="font-heading text-xl text-[#171310]">Add a Genuine Customer Review</h3>
        <p className="text-sm text-[#8f8571] mt-1">
          Copy real reviews from your Google Business Profile and paste them here. The 3 most recent appear on the home page. Only add authentic reviews from real customers.
        </p>
        <form onSubmit={addReview} className="grid sm:grid-cols-[1fr_140px] gap-4 mt-5">
          <Input
            value={reviewForm.author}
            onChange={(e) => setReviewForm({ ...reviewForm, author: e.target.value })}
            placeholder="Customer name (e.g. Priya S.)"
            className="rounded-none border-[#d8cdb5] h-11"
            data-testid="admin-review-author-input"
          />
          <Select value={String(reviewForm.rating)} onValueChange={(v) => setReviewForm({ ...reviewForm, rating: Number(v) })}>
            <SelectTrigger className="rounded-none border-[#d8cdb5] h-11 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
              {[5, 4, 3, 2, 1].map((r) => <SelectItem key={r} value={String(r)}>{r} star{r > 1 ? "s" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <textarea
            value={reviewForm.text}
            onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
            placeholder="Paste the review text exactly as the customer wrote it..."
            rows={3}
            className="sm:col-span-2 border border-[#d8cdb5] bg-white px-3 py-2 text-sm outline-none focus:border-[#a8842c] transition-colors"
            data-testid="admin-review-text-input"
          />
          <Button type="submit" className="sm:col-span-2 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300" data-testid="admin-add-review-btn">
            Add Review
          </Button>
        </form>
      </div>

      {reviews.length === 0 ? (
        <p className="text-[#8f8571] text-sm py-6 text-center">No reviews added yet. Paste your first genuine Google review above.</p>
      ) : (
        <div className="space-y-4" data-testid="admin-reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-[#eee6d6] p-5 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <p className="font-heading text-lg text-[#171310]">{r.author}</p>
                  <span className="flex gap-0.5">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={`${r.id}-star-${i}`} size={13} className="fill-[#c9a24b] text-[#c9a24b]" />
                    ))}
                  </span>
                  <span className="text-xs text-[#9c9482]">via {r.source || "Google"} · {fmtDate(r.createdAt)}</span>
                </div>
                <p className="text-sm text-[#5c5240] mt-2 leading-relaxed">{r.text}</p>
              </div>
              <Button variant="outline" size="icon" onClick={() => deleteReview(r.id)} className="rounded-none border-[#d8cdb5] h-9 w-9 shrink-0 text-[#8c2f1b] hover:bg-[#8c2f1b] hover:text-white" aria-label="Delete review">
                <Trash2 size={15} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
